import React from "react";
import { Box, Typography } from "@mui/material";
import { GoogleIntent, GoogleReturnContext, startGoogleSignIn } from "./googleSignIn";
import { LastUsedBadge } from "./LastUsedBadge";

interface Props {
    intent: GoogleIntent;
    /** Path + query to return to after Google (defaults to the current page). */
    returnPath?: string;
    rmnSlug?: string;
    /** Preselects this Google account (the checkout or funnel email). */
    loginHint?: string;
    /** "Continue with Google" for login, "Sign up with Google" for registration. */
    label?: string;
    /** Called before leaving; return false to stay (the expert form confirms when dirty). */
    onBeforeStart?: () => boolean;
    /** Dark pages (brand registration) invert the button's colours. */
    dark?: boolean;
}

const DEFAULT_TEXT: Partial<Record<GoogleIntent, string>> = {
    login: "Continue with Google",
    connect: "Connect Google"
};

// The "G" mark from Google's sign-in branding guidelines, drawn inline so
// no script or asset is loaded for it.
const GoogleMark = () => (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
);

export function GoogleButton({ intent, returnPath, rmnSlug, loginHint, label, onBeforeStart, dark }: Props) {
    const start = () => {
        if (onBeforeStart && onBeforeStart() === false) return;
        const ctx: GoogleReturnContext = {
            intent,
            returnPath: returnPath || `${window.location.pathname}${window.location.search}`,
            ...(rmnSlug ? { rmnSlug } : {})
        };
        startGoogleSignIn(ctx, loginHint);
    };

    const text = label || DEFAULT_TEXT[intent] || "Sign up with Google";

    return (
        <Box className="w-full flex flex-col items-center gap-3">
            <Box className="relative w-full">
                <button
                    type="button"
                    onClick={start}
                    className="w-full flex items-center justify-center gap-3 rounded-full py-3 px-4 transition-opacity hover:opacity-90"
                    style={{
                        border: `1px solid ${dark ? "rgba(255,255,255,0.4)" : "#dadce0"}`,
                        backgroundColor: "#fff",
                        color: "#1f1f1f",
                        fontFamily: "Roboto, Lato, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        cursor: "pointer"
                    }}
                >
                    <GoogleMark />
                    {text}
                </button>
                {intent === "login" && <LastUsedBadge method="google" />}
            </Box>
            {intent !== "connect" && (
                <Typography
                    component="span"
                    sx={{ fontSize: "11px", color: dark ? "rgba(255,255,255,0.5)" : "var(--text-2, #868686)", textAlign: "center" }}
                >
                    {intent === "login" ? "Signed up with Google? Use Continue with Google." : "or"}
                </Typography>
            )}
        </Box>
    );
}
